import { Theme } from '@logto/schemas';
import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { SignInExperienceResponse, Platform } from '@/types';

import type { PageContextType } from './PageContext';
import MainContext from './PageContext';

type Props = {
  readonly children: React.ReactNode;
  readonly preset?: Partial<
    Pick<
      PageContextType,
      | 'theme'
      | 'toast'
      | 'loading'
      | 'platform'
      | 'termsAgreement'
      | 'experienceSettings'
      | 'isPreview'
    >
  >;
};

// TDA: remember the user's choice. Upstream only reads the theme the server
// rendered, so toggling was lost on every reload.
//
// The write lives in the toggle (Layout/AppLayout/TopBar), NOT in `setTheme`:
// `use-theme` and `use-sign-in-experience` also call `setTheme` from the system
// preference on mount, and persisting those would overwrite the user's choice
// on every load — measured, it flipped straight back.
/* ⚠️ Re-exported, not defined here. The canonical copy lives in
   `shared/components/ThemeToggle` because the Account Center imports it through
   `@experience/shared` and cannot reach into this folder. Two definitions of one
   storage key means the two apps eventually disagree about where the choice is
   kept, and the symptom is a theme that resets when you cross between them. */
export { themeStorageKey } from '@/shared/components/ThemeToggle';
// eslint-disable-next-line import/no-duplicates -- re-export above is the public name; this binds it locally.
import { themeStorageKey } from '@/shared/components/ThemeToggle';

const getInitialTheme = () => {
  const stored = localStorage.getItem(themeStorageKey);

  if (stored === Theme.Dark || stored === Theme.Light) {
    return stored;
  }

  if (document.documentElement.dataset.theme === Theme.Dark) {
    return Theme.Dark;
  }

  return Theme.Light;
};

const getInitialPlatform = (): Platform =>
  document.body.classList.contains('mobile') ? 'mobile' : 'web';

const PageContextProvider = ({ children, preset }: Props) => {
  const [searchParameters] = useSearchParams();

  const [loading, setLoading] = useState(preset?.loading ?? false);
  const [toast, setToast] = useState(preset?.toast ?? '');
  const [theme, setTheme] = useState<Theme>(preset?.theme ?? getInitialTheme);

  const [platform, setPlatform] = useState<Platform>(preset?.platform ?? getInitialPlatform);
  const [termsAgreement, setTermsAgreement] = useState(preset?.termsAgreement ?? false);
  const [experienceSettings, setExperienceSettings] = useState<
    SignInExperienceResponse | undefined
  >(preset?.experienceSettings ?? undefined);

  const isPreview = searchParameters.get('preview') === 'true';

  const pageContext = useMemo<PageContextType>(
    () => ({
      theme,
      toast,
      loading,
      platform,
      termsAgreement,
      experienceSettings,
      isPreview,
      setTheme,
      setLoading,
      setToast,
      setPlatform,
      setTermsAgreement,
      setExperienceSettings,
    }),
    [experienceSettings, isPreview, loading, platform, termsAgreement, theme, toast]
  );

  return <MainContext.Provider value={pageContext}>{children}</MainContext.Provider>;
};

export default PageContextProvider;
